import * as Sentry from "@sentry/react";
import invariant from "tiny-invariant";

const environment = process.env.ENVIRONMENT;
invariant(environment);

class SentryLogger {
  constructor(private environment: string) {
    this.setUser = this.setUser.bind(this);
    this.log = this.log.bind(this);
    this.error = this.error.bind(this);
    this.isRemoteLoggingEnabled = this.isRemoteLoggingEnabled.bind(this);

    if (this.isRemoteLoggingEnabled()) {
      Sentry.init({
        // when SENTRY_DSN is not set, the SDK will not be initialized
        // and that is ok on dev environments
        dsn: process.env.SENTRY_DSN,
        environment: this.environment,
        ignoreErrors: [/Failed to fetch/i],
        integrations: [
          Sentry.replayIntegration(),
          Sentry.httpClientIntegration(),
          Sentry.browserTracingIntegration(),
        ],
        normalizeDepth: 10,
        release: process.env.RELEASE_ID,
        replaysOnErrorSampleRate: 1.0,
        replaysSessionSampleRate: 0.01,
        sendDefaultPii: false,
        tracePropagationTargets: [
          /cleverfarm\.cz/,
          /cleverfarm\.ag/,
          /mymex\.cz/,
          /agronutriconsult\.sk/,
          /^\//, // matches anything on the same URL
        ],
        tracesSampleRate: 0.25,
      });
    }
  }

  public setContext(context: Parameters<typeof Sentry.setContext>[1]) {
    if (!this.isRemoteLoggingEnabled()) {
      return;
    }

    Sentry.setContext("details", context);
  }

  public setUser(user: Sentry.User | null) {
    if (!this.isRemoteLoggingEnabled()) {
      return;
    }
    Sentry.setUser(user);
  }

  public log(...args: unknown[]) {
    // eslint-disable-next-line no-console
    console.log(...args);
  }

  public error(e: Error | unknown | undefined, ...args: unknown[]) {
    // eslint-disable-next-line no-console
    console.error(e, ...args);

    if (this.isRemoteLoggingEnabled()) {
      return Sentry.captureException(e);
    }
  }

  private isRemoteLoggingEnabled() {
    return window.location.hostname !== "localhost";
  }
}

const logger = new SentryLogger(environment);

export { logger };
